import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@nucleus-commerce/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useProductVendors } from '../hooks/use-product-vendors';

const MAX_VENDOR_LENGTH = 120;
const NAVIGATION_KEYS = new Set(['ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']);

export interface VendorComboboxProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/**
 * Vendor picker over the vendors the organisation's products already use, most used first, with
 * "Add" for a new name. A name that differs from an existing vendor only by case picks the
 * existing spelling, so the vendor filter does not split one supplier in two.
 */
export function VendorCombobox({
  value,
  onChange,
  onBlur,
  disabled,
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: VendorComboboxProps) {
  const { t } = useTranslation('products');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const typed = search.trim();
  const settled = useDebouncedValue(typed, 200);
  const vendors = useProductVendors(settled, open);
  const items = vendors.data ?? [];
  const match = items.find((v) => v.value.toLowerCase() === typed.toLowerCase());
  // Only once the list answers what was typed, so a slow response never offers a duplicate.
  const canCreate =
    typed.length > 0 &&
    settled === typed &&
    !vendors.isFetching &&
    !match &&
    typed.toLowerCase() !== value.toLowerCase();
  const currentMissing = value !== '' && typed === '' && !items.some((v) => v.value === value);

  // cmdk moves its highlight to the first row whenever the highlighted row goes away (the list
  // loading, a new name typed), which could leave Enter on "No vendor". The highlight is derived
  // here instead, and only arrow keys or the pointer may move it. cmdk 1.1.1 keeps its own pick
  // even when told no and re-reads `value` only when the string changes; it trims the value, so a
  // toggled trailing space makes it take the derived highlight back.
  const [active, setActive] = useState('');
  const navigating = useRef(false);
  const [resync, setResync] = useState(false);
  const rows = [
    ...(canCreate ? [`create:${typed}`] : []),
    ...(currentMissing ? [`current:${value}`] : []),
    ...items.map((v) => `vendor:${v.value}`),
    ...(value ? ['clear'] : []),
  ];
  const current = currentMissing ? `current:${value}` : `vendor:${value}`;
  const best = canCreate
    ? `create:${typed}`
    : match
      ? `vendor:${match.value}`
      : typed === '' && rows.includes(current)
        ? current
        : (rows.find((row) => row !== 'clear') ?? '');
  const highlighted = rows.includes(active) ? active : best;

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
    setSearch('');
    setActive('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearch('');
          setActive('');
          onBlur?.();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', !value && 'text-muted-foreground')}
        >
          <span className="truncate">{value || t('vendorPicker.placeholder')}</span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0" align="start">
        <Command
          shouldFilter={false}
          value={resync ? `${highlighted} ` : highlighted}
          onValueChange={(next) => {
            if (navigating.current) setActive(next);
            else if (next !== highlighted) setResync((r) => !r);
            navigating.current = false;
          }}
          onKeyDown={(event) => {
            navigating.current = NAVIGATION_KEYS.has(event.key);
          }}
        >
          <CommandInput
            placeholder={t('vendorPicker.search')}
            value={search}
            onValueChange={(next) => {
              navigating.current = false;
              setSearch(next);
              setActive('');
            }}
            maxLength={MAX_VENDOR_LENGTH}
          />
          <CommandList
            className="max-h-72"
            onPointerMoveCapture={() => {
              navigating.current = true;
            }}
          >
            {vendors.isPending ? (
              <p className="text-muted-foreground py-6 text-center text-sm">
                {t('vendorPicker.loading')}
              </p>
            ) : (
              <CommandEmpty>
                {typed ? t('vendorPicker.noMatch') : t('vendorPicker.empty')}
              </CommandEmpty>
            )}
            {canCreate ? (
              <CommandGroup>
                <CommandItem value={`create:${typed}`} onSelect={() => choose(typed)}>
                  <Plus className="size-4" aria-hidden />
                  <span className="truncate">{t('vendorPicker.create', { name: typed })}</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {currentMissing || items.length > 0 ? (
              <CommandGroup heading={t('vendorPicker.existing')}>
                {currentMissing ? (
                  <CommandItem value={`current:${value}`} onSelect={() => choose(value)}>
                    <Check className="size-4" aria-hidden />
                    <span className="flex-1 truncate">{value}</span>
                  </CommandItem>
                ) : null}
                {items.map((vendor) => (
                  <CommandItem
                    key={vendor.value}
                    value={`vendor:${vendor.value}`}
                    onSelect={() => choose(vendor.value)}
                  >
                    <Check
                      className={cn('size-4', vendor.value === value ? 'opacity-100' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="flex-1 truncate">{vendor.value}</span>
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {t('vendorPicker.count', { count: vendor.count })}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {value ? (
              <CommandGroup>
                <CommandItem value="clear" onSelect={() => choose('')}>
                  <X className="size-4" aria-hidden />
                  <span className="text-muted-foreground">{t('vendorPicker.clear')}</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
